import { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Button,
  Text,
  Group,
  Select,
  Loader,
  Badge,
  Paper,
  Title,
  ActionIcon,
  Tooltip,
} from "@mantine/core";

import {
  IconArchiveOff,
  IconDownload,
  IconTrash,
  IconReload,
  IconCheck,
  IconChevronDown,
  IconX,
  IconEye,
  IconEyeOff,
} from "@tabler/icons-react";
import { saveAs } from "file-saver";
import { showNotification } from "@mantine/notifications";
import { useDocumentModalStore } from "./useDocumentModalStore.ts";
import { useStartOCRJob } from "../../../hooks/useStartOCR.ts";
import { useDeleteDoc } from "@pages/ClientProfile/hooks/useDeleteDoc.ts";
import { useRecategorizeDocs } from "@pages/ClientProfile/hooks/useRecategorizeDoc.ts";
import { useProfileStore } from "@pages/ClientProfile/store/profileStore.ts";
import { unarchiveDoc } from "@pages/ClientProfile/api.ts";
import { findDocumentById } from "@pages/ClientProfile/utils.tsx";
import { FORM2ID, forms } from "@/constants.ts";
import type { DocumentUserType } from "@pages/ClientProfile/types.ts";
import styles from "../../../styles/DocumentPopover.module.scss";

// Create reverse mapping from backend FormType to frontend form labels
const ID2FORM: Record<string, string> = Object.entries(FORM2ID).reduce(
  (acc, [formLabel, formId]) => {
    acc[formId] = formLabel;
    return acc;
  },
  {} as Record<string, string>
);

type ActionType = "delete" | null;

export const DocumentActionsModal = () => {
  const { isDocumentActionsOpen, closeDocumentActions, setDocumentPopoverId } =
    useDocumentModalStore();
  const {
    activeDocument,
    setActiveDocument,
    setSelectedDoc,
    setOcrProcessingDocId,
    refetch,
    toggleExtractionEnabled,
  } = useProfileStore();

  const [currentAction, setCurrentAction] = useState<ActionType>(null);
  const [unarchiveLoading, setUnarchiveLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState("");
  const [userType, setUserType] = useState<DocumentUserType>("taxClient");
  const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);

  const documentId = activeDocument ? parseInt(activeDocument) : null;
  const { mutate: startOCRJob, isPending: isStartingOCR } = useStartOCRJob(
    documentId?.toString() ?? "0"
  );
  const { deleteDoc, isDeleting } = useDeleteDoc();
  const { recategorize } = useRecategorizeDocs();

  // Get current document
  const currentDocument = activeDocument
    ? findDocumentById(activeDocument)
    : null;

  // Set default values when modal opens
  useEffect(() => {
    if (isDocumentActionsOpen && activeDocument) {
      // Get the current document (try to refetch if not available)
      const doc = findDocumentById(activeDocument);

      console.log("DocumentActionsModal - Setting form type:", {
        activeDocument,
        doc,
        backendFormType: doc?.documentType,
        ID2FORM,
      });

      if (doc) {
        // Set default form selection based on current document type
        const backendFormType = doc.documentType;
        const frontendFormLabel = backendFormType
          ? ID2FORM[backendFormType]
          : "";

        console.log("DocumentActionsModal - Form mapping:", {
          backendFormType,
          frontendFormLabel,
          isInFormsArray:
            frontendFormLabel && forms.includes(frontendFormLabel),
          formsArray: forms,
        });

        if (frontendFormLabel && forms.includes(frontendFormLabel)) {
          setSelectedForm(frontendFormLabel);
        } else {
          setSelectedForm("Other");
        }

        // Set default user type
        if (doc.userType) {
          setUserType(doc.userType);
        } else {
          setUserType("taxClient");
        }
      } else {
        // Fallback if document not found
        setSelectedForm("Other");
        setUserType("taxClient");
      }
    } else if (!isDocumentActionsOpen) {
      // Reset when modal closes
      setCurrentAction(null);
      setSelectedForm("Other");
      setUserType("taxClient");
      setIsUpdatingDocument(false);
    }
  }, [isDocumentActionsOpen, activeDocument]);

  const handleClose = () => {
    setCurrentAction(null);
    setIsUpdatingDocument(false);
    setDocumentPopoverId(null);
    closeDocumentActions();
  };

  const handleDownload = async () => {
    if (!currentDocument?.presignedUrl) {
      showNotification({
        color: "red",
        message: "Presigned URL is not available.",
      });
      return;
    }

    setDownloadLoading(true);
    try {
      const response = await fetch(currentDocument.presignedUrl);
      const blob = await response.blob();
      saveAs(blob, currentDocument?.label ?? "default-filename");

      showNotification({
        color: "green",
        message: "File downloaded successfully",
        icon: <IconCheck size={16} />,
      });

      handleClose();
    } catch (error) {
      console.error("Error downloading document:", error);
      showNotification({
        color: "red",
        message: "Error downloading document",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!activeDocument) {
      showNotification({
        color: "orange",
        message: "Cannot delete because no document selected",
      });
      return;
    }

    const success = await deleteDoc(activeDocument);
    if (success) {
      setActiveDocument(null);
      setSelectedDoc(null);
      handleClose();
    }
  };

  const handleRecategorize = async () => {
    if (!activeDocument) {
      showNotification({
        color: "orange",
        message: "Cannot recategorize because no document selected",
      });
      return;
    }

    if (!selectedForm || selectedForm === "") {
      showNotification({
        color: "orange",
        message: "Cannot recategorize because no form selected",
      });
      return;
    }

    if (!currentDocument) {
      showNotification({
        color: "red",
        message: "Cannot find document information.",
      });
      return;
    }

    // Check if form type is actually changing
    const currentFormType = currentDocument.documentType;
    const newFormType = FORM2ID[selectedForm];
    const shouldTriggerOCR = currentFormType !== newFormType;

    setIsUpdatingDocument(true);

    // If OCR will be triggered, mark as processing and close immediately
    if (shouldTriggerOCR) {
      setOcrProcessingDocId(activeDocument);
      setIsUpdatingDocument(false);
      handleClose();

      showNotification({
        color: "blue",
        message: "Document is being recategorized and re-processed...",
      });
    }

    // Start the recategorization (don't await it for OCR cases)
    const recategorizePromise = recategorize({
      documentId: activeDocument,
      newForm: selectedForm,
      userType: userType,
    });

    // For non-OCR cases, we need to wait and handle the response
    if (!shouldTriggerOCR) {
      try {
        const success = await recategorizePromise;

        if (!success) {
          showNotification({
            color: "red",
            message: "An error occurred while updating document.",
          });
        } else {
          showNotification({
            color: "green",
            message: "Document owner was successfully updated!",
          });
        }
      } finally {
        setIsUpdatingDocument(false);
        handleClose();
      }
    } else {
      // For OCR cases, handle errors in background without blocking UI
      recategorizePromise.catch((error) => {
        console.error("Recategorization failed:", error);
        setOcrProcessingDocId(null);
        showNotification({
          color: "red",
          message: "An error occurred while recategorizing document.",
        });
      });
    }
  };

  const handleRefetchOCR = () => {
    if (documentId && activeDocument) {
      // Mark document as processing immediately
      setOcrProcessingDocId(activeDocument);

      // Close modal immediately
      handleClose();

      // Show notification that processing has started
      showNotification({
        title: "OCR Processing Started",
        message:
          "Document is being re-processed. You'll be notified when it's complete.",
        color: "blue",
        icon: <IconReload size={16} />,
      });

      // Start the OCR job
      startOCRJob(undefined, {
        onError: (error: unknown) => {
          // Clear processing state on error
          setOcrProcessingDocId(null);

          if (error instanceof Error) {
            console.error("OCR job failed to start:", error);
          } else {
            console.error("OCR job failed to start with an unknown error.");
          }
        },
      });
    }
  };

  const handleUnarchive = async () => {
    if (!activeDocument) return;

    setUnarchiveLoading(true);
    try {
      await unarchiveDoc(activeDocument);
      showNotification({
        color: "green",
        message: "Document unarchived successfully.",
      });
      handleClose();
      refetch();
    } catch (error) {
      console.error("Error unarchiving document:", error);
      showNotification({
        color: "red",
        message: "Failed to unarchive document.",
      });
    } finally {
      setUnarchiveLoading(false);
    }
  };

  const renderActionContent = () => {
    if (currentAction === "delete") {
      return (
        <Stack>
          <Title order={3}>Delete file?</Title>
          <Text>
            Are you sure you want to delete this file? This action cannot be
            undone.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={() => setCurrentAction(null)}>
              Cancel
            </Button>
            <Button color="#DC373A" onClick={handleDelete} loading={isDeleting}>
              Delete
            </Button>
          </Group>
        </Stack>
      );
    }

    // Main document information view with reclassification fields
    return (
      <Stack gap="lg">
        {/* Top toolbar with action buttons on left, close button on right */}
        <Group justify="space-between" align="center">
          <Group>
            <Tooltip label="Delete this document">
              <ActionIcon
                variant="light"
                color="red"
                size="lg"
                onClick={() => setCurrentAction("delete")}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Download">
              <ActionIcon
                variant="light"
                color="blue"
                size="lg"
                onClick={handleDownload}
                loading={downloadLoading}
              >
                <IconDownload size={18} />
              </ActionIcon>
            </Tooltip>
            {currentDocument?.archived && (
              <Tooltip label="Unarchive document">
                <ActionIcon
                  variant="light"
                  color="orange"
                  size="lg"
                  onClick={handleUnarchive}
                  loading={unarchiveLoading}
                >
                  <IconArchiveOff size={18} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="Refetch OCR">
              <ActionIcon
                variant="light"
                color="orange"
                size="lg"
                onClick={handleRefetchOCR}
                loading={isStartingOCR}
              >
                {isStartingOCR ? (
                  <Loader size={18} />
                ) : (
                  <IconReload size={18} />
                )}
              </ActionIcon>
            </Tooltip>
          </Group>
          <Group>
            <Tooltip label="Close">
              <ActionIcon
                variant="light"
                color="gray"
                size="lg"
                onClick={handleClose}
              >
                <IconX size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        {/*
        DOCUMENT DETAILS
        ---
        Filename, document type,
        */}
        <Title order={3}>Document Information</Title>
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                File Name:
              </Text>
              <Text size="sm" fw={500}>
                {currentDocument?.label}
              </Text>
            </Group>
            {currentDocument?.documentType && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Document Type:
                </Text>
                <Text size="sm" fw={500}>
                  {ID2FORM[currentDocument.documentType] ||
                    currentDocument.documentType}
                </Text>
              </Group>
            )}
            {currentDocument?.userType && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Applies To:
                </Text>
                <Badge
                  size="sm"
                  radius="sm"
                  c={
                    currentDocument.userType === "spouse"
                      ? "#0077CC"
                      : currentDocument.userType === "dependent"
                        ? "#8B5A2B"
                        : "#0077CC"
                  }
                  color={
                    currentDocument.userType === "spouse"
                      ? "#ACD3F0"
                      : currentDocument.userType === "dependent"
                        ? "#E4C49F"
                        : "#ACD3F0"
                  }
                  style={{ textTransform: "capitalize" }}
                >
                  {currentDocument.userType === "taxClient"
                    ? "Taxpayer"
                    : currentDocument.userType}
                </Badge>
              </Group>
            )}
          </Stack>
        </Paper>

        {/* Extraction Toggle */}
        {currentDocument?.ocrExtractions &&
          currentDocument.ocrExtractions.length > 0 && (
            <Paper withBorder p="md" radius="md">
              <Text fw={500} size="sm" mb="xs">
                Extractions
              </Text>
              <Stack gap="xs">
                {currentDocument.ocrExtractions.map((ext) => {
                  const isDisabled = ext.enabled === false;
                  return (
                    <Group key={ext.id} justify="space-between">
                      <Group gap="xs">
                        <Badge
                          size="sm"
                          variant="light"
                          color={isDisabled ? "gray" : "blue"}
                        >
                          {ext.form}
                        </Badge>
                        <Text
                          size="sm"
                          style={{
                            textDecoration: isDisabled
                              ? "line-through"
                              : undefined,
                            opacity: isDisabled ? 0.6 : 1,
                          }}
                        >
                          {ext.title || ext.form}
                        </Text>
                      </Group>
                      <Tooltip
                        label={
                          isDisabled
                            ? "Enable extraction"
                            : "Disable extraction"
                        }
                      >
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color={isDisabled ? "green" : "red"}
                          onClick={() =>
                            toggleExtractionEnabled(ext.id, !ext.enabled)
                          }
                        >
                          {isDisabled ? (
                            <IconEye size={16} />
                          ) : (
                            <IconEyeOff size={16} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  );
                })}
              </Stack>
            </Paper>
          )}

        <Stack gap="md">
          <div style={{ textAlign: "left" }}>
            <Text fw={500} size="lg" mb={6}>
              Re-Categorize Document
            </Text>
            <Text size="sm" c="dimmed" mb={8}>
              Move this document to the correct category or person if it was
              added to the wrong one.
            </Text>
            <Text
              size="xs"
              c="yellow.6"
              style={{
                color: "rgba(255, 212, 59, 0.1)",
                backgroundColor: "rgba(255, 212, 59, 0.1)",
                padding: "6px 12px",
                borderRadius: "4px",
                border: "1px solid rgba(255, 212, 59, 0.5)",
                display: "inline-block",
              }}
            >
              Editing the document type will override any changes made to the
              current document's OCR information as new details are extracted.
            </Text>
          </div>

          <div className={styles.formWrapper}>
            <label htmlFor="form-type">Document Type</label>
            <select
              id="form-type"
              value={selectedForm}
              onChange={(e) => setSelectedForm(e.target.value)}
            >
              <option value="" disabled>
                Select a form
              </option>
              {forms.map((form) => (
                <option key={form} value={form}>
                  {form}
                </option>
              ))}
            </select>
            <span className={styles.chevron}>
              <IconChevronDown />
            </span>
          </div>

          <Select
            label="Applies To"
            value={userType}
            onChange={(value) => setUserType(value as DocumentUserType)}
            data={[
              { value: "taxClient", label: "Taxpayer" },
              { value: "spouse", label: "Spouse" },
              { value: "dependent", label: "Dependent" },
            ]}
            required
          />

          <Group justify="right">
            <Button
              color="#0077CC"
              onClick={handleRecategorize}
              disabled={!selectedForm || selectedForm === ""}
              loading={isUpdatingDocument}
            >
              Update
            </Button>
          </Group>
        </Stack>
      </Stack>
    );
  };

  return (
    <Modal
      opened={isDocumentActionsOpen}
      onClose={handleClose}
      radius={12}
      centered
      closeOnClickOutside={
        !isDeleting &&
        !isUpdatingDocument &&
        !downloadLoading &&
        !unarchiveLoading
      }
      closeOnEscape={
        !isDeleting &&
        !isUpdatingDocument &&
        !downloadLoading &&
        !unarchiveLoading
      }
      withCloseButton={false}
      size="lg"
      overlayProps={{
        backgroundOpacity: 0.1,
        blur: 1,
        color: "#000",
      }}
    >
      <div style={{ padding: "1.5rem" }}>{renderActionContent()}</div>
    </Modal>
  );
};
